import React, { useState, useRef, useEffect } from 'react';
import { 
  FileText, Plus, Edit2, Trash2, Download, ArrowLeft, CheckCircle, 
  Save, Folder, Table, Grid, Search, Filter, X, Upload, Eye
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import api from '../lib/api';
import { toast } from 'sonner';

// ==================== SIGNATURE PAD ====================
const SignaturePad = ({ onSave, label }) => {
  const canvasRef = useRef(null);
  const [drawing, setDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = '#1A1A1A';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
  }, []);

  const getPos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const touch = e.touches ? e.touches[0] : e;
    return { x: touch.clientX - rect.left, y: touch.clientY - rect.top };
  };

  const start = (e) => { e.preventDefault(); setDrawing(true); const { x, y } = getPos(e); canvasRef.current.getContext('2d').beginPath(); canvasRef.current.getContext('2d').moveTo(x, y); };
  const draw = (e) => { if (!drawing) return; e.preventDefault(); const { x, y } = getPos(e); const ctx = canvasRef.current.getContext('2d'); ctx.lineTo(x, y); ctx.stroke(); setHasSignature(true); };
  const stop = () => setDrawing(false);
  const clear = () => { const ctx = canvasRef.current.getContext('2d'); ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, canvasRef.current.width, canvasRef.current.height); setHasSignature(false); };
  const save = () => { if (!hasSignature) { toast.error('Please sign first'); return; } onSave(canvasRef.current.toDataURL('image/png')); toast.success('Signature captured'); };

  return (
    <div>
      <Label className="text-white/70 text-sm mb-2 block">{label || 'Signature'}</Label>
      <div className="border border-white/20 rounded-lg overflow-hidden bg-white">
        <canvas ref={canvasRef} width={400} height={150}
          onMouseDown={start} onMouseMove={draw} onMouseUp={stop} onMouseLeave={stop}
          onTouchStart={start} onTouchMove={draw} onTouchEnd={stop}
          className="w-full cursor-crosshair touch-none" style={{ maxWidth: '400px' }} />
      </div>
      <div className="flex gap-2 mt-2">
        <Button onClick={clear} variant="outline" size="sm" className="border-white/20 text-white/60 text-xs">Clear</Button>
        <Button onClick={save} size="sm" className="bg-electric-teal text-primary-dark text-xs font-bold">
          <CheckCircle className="w-3 h-3 mr-1" /> Accept
        </Button>
      </div>
    </div>
  );
};

// ==================== FORM TEMPLATE EDITOR ====================
const FormTemplateEditor = ({ template, onSave, onClose }) => {
  const [form, setForm] = useState(template || {
    name: '', description: '', category: 'general', fields: [], pdf_url: '', thumbnail_url: '', is_active: true
  });
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const fieldTypes = [
    { value: 'text', label: 'Text' },
    { value: 'number', label: 'Number' },
    { value: 'date', label: 'Date' },
    { value: 'email', label: 'Email' },
    { value: 'phone', label: 'Phone' },
    { value: 'textarea', label: 'Text Area' },
    { value: 'select', label: 'Dropdown' },
    { value: 'checkbox', label: 'Checkbox' },
    { value: 'signature', label: 'Signature' },
  ];

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Please select a PDF file');
      return;
    }
    setUploading(true);
    try {
      const res = await api.admin.uploadPdf(file);
      const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${res.data.url}`;
      setForm({ ...form, pdf_url: fullUrl });
      toast.success('PDF uploaded successfully!');
    } catch (err) {
      toast.error('Upload failed');
      console.error(err);
    }
    setUploading(false);
  };

  const addField = () => {
    setForm({
      ...form,
      fields: [...form.fields, { name: '', label: '', field_type: 'text', required: false, options: [], order: form.fields.length }]
    });
  };

  const updateField = (index, key, value) => {
    const fields = [...form.fields];
    fields[index] = { ...fields[index], [key]: value };
    setForm({ ...form, fields });
  };

  const removeField = (index) => {
    setForm({ ...form, fields: form.fields.filter((_, i) => i !== index) });
  };

  const handleSave = async () => {
    if (!form.name) { toast.error('Form name required'); return; }
    try {
      if (template?.id) {
        await api.admin.updateFormTemplate(template.id, form);
        toast.success('Form template updated');
      } else {
        await api.admin.createFormTemplate(form);
        toast.success('Form template created');
      }
      onSave();
    } catch (e) {
      toast.error('Failed to save');
    }
  };

  return (
    <div className="space-y-6">
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <Label className="text-white/70">Form Name *</Label>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="bg-primary-dark border-white/20 text-white mt-1" placeholder="Panel Schedule" />
        </div>
        <div>
          <Label className="text-white/70">Category</Label>
          <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
            <SelectTrigger className="bg-primary-dark border-white/20 text-white mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="general">General</SelectItem>
              <SelectItem value="estimate">Estimate</SelectItem>
              <SelectItem value="invoice">Invoice</SelectItem>
              <SelectItem value="permit">Permit</SelectItem>
              <SelectItem value="waiver">Waiver</SelectItem>
              <SelectItem value="panel">Panel/Technical</SelectItem>
              <SelectItem value="custom">Custom</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      
      <div>
        <Label className="text-white/70">Description</Label>
        <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
          className="bg-primary-dark border-white/20 text-white mt-1" rows={2} placeholder="What is this form for?" />
      </div>

      {/* PDF Upload Section */}
      <div className="bg-white/5 p-4 rounded-lg border border-white/10">
        <Label className="text-white/70 mb-3 block">PDF Template</Label>
        <div className="flex flex-col sm:flex-row gap-3">
          <input 
            type="file" 
            ref={fileInputRef}
            accept=".pdf"
            onChange={handleFileUpload}
            className="hidden"
          />
          <Button 
            type="button"
            onClick={() => fileInputRef.current?.click()}
            variant="outline"
            className="border-electric-teal/50 text-electric-teal hover:bg-electric-teal/10"
            disabled={uploading}
          >
            <Upload className="w-4 h-4 mr-2" />
            {uploading ? 'Uploading...' : 'Upload PDF'}
          </Button>
          {form.pdf_url && (
            <a 
              href={form.pdf_url} 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-electric-teal hover:underline text-sm"
            >
              <Eye className="w-4 h-4" />
              View Current PDF
            </a>
          )}
        </div>
        {form.pdf_url && (
          <p className="text-white/40 text-xs mt-2 truncate">URL: {form.pdf_url}</p>
        )}
      </div>

      {/* Fields */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <Label className="text-white/70">Form Fields</Label>
          <Button onClick={addField} size="sm" className="bg-electric-teal/20 text-electric-teal">
            <Plus className="w-3 h-3 mr-1" /> Add Field
          </Button>
        </div>
        
        <div className="space-y-3 max-h-80 overflow-y-auto">
          {form.fields.map((field, i) => (
            <div key={i} className="bg-white/5 p-3 rounded-lg border border-white/10">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <Input value={field.name} onChange={(e) => updateField(i, 'name', e.target.value)}
                  placeholder="field_name" className="bg-primary-dark border-white/10 text-white text-sm" />
                <Input value={field.label} onChange={(e) => updateField(i, 'label', e.target.value)}
                  placeholder="Display Label" className="bg-primary-dark border-white/10 text-white text-sm" />
                <Select value={field.field_type} onValueChange={(v) => updateField(i, 'field_type', v)}>
                  <SelectTrigger className="bg-primary-dark border-white/10 text-white text-sm h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {fieldTypes.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <div className="flex items-center gap-2">
                  <label className="flex items-center gap-1 text-white/60 text-xs">
                    <input type="checkbox" checked={field.required} onChange={(e) => updateField(i, 'required', e.target.checked)} />
                    Required
                  </label>
                  <Button onClick={() => removeField(i)} variant="ghost" size="sm" className="text-red-400 ml-auto p-1">
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              {field.field_type === 'select' && (
                <Input value={(field.options || []).join(', ')} 
                  onChange={(e) => updateField(i, 'options', e.target.value.split(',').map(s => s.trim()))}
                  placeholder="Option 1, Option 2, Option 3" 
                  className="bg-primary-dark border-white/10 text-white text-sm mt-2" />
              )}
            </div>
          ))}
          {form.fields.length === 0 && (
            <p className="text-white/30 text-sm text-center py-4">No fields yet. Click "Add Field" to start.</p>
          )}
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-4 border-t border-white/10">
        <Button onClick={onClose} variant="outline" className="border-white/20 text-white/60">Cancel</Button>
        <Button onClick={handleSave} className="bg-electric-teal text-primary-dark font-bold">
          <Save className="w-4 h-4 mr-1" /> Save Template
        </Button>
      </div>
    </div>
  );
};

// ==================== FORM FILLER (For completing a form submission) ====================
const FormFiller = ({ submission, template, onSave, onClose }) => {
  const [data, setData] = useState(submission?.data || {});
  const [signature, setSignature] = useState(submission?.signature_data || null);
  const [status, setStatus] = useState(submission?.status || 'draft');

  const handleSave = async () => {
    try {
      await api.admin.updateFormSubmission(submission.id, data, status, signature);
      toast.success('Form saved');
      onSave();
    } catch (e) {
      toast.error('Failed to save');
    }
  };

  const renderField = (field) => {
    const value = data[field.name] || '';
    const onChange = (v) => setData({ ...data, [field.name]: v });

    switch (field.field_type) {
      case 'textarea':
        return <Textarea value={value} onChange={(e) => onChange(e.target.value)}
          className="bg-primary-dark border-white/20 text-white" rows={3} />;
      case 'select':
        return (
          <Select value={value} onValueChange={onChange}>
            <SelectTrigger className="bg-primary-dark border-white/20 text-white"><SelectValue placeholder="Select..." /></SelectTrigger>
            <SelectContent>
              {(field.options || []).map(opt => <SelectItem key={opt} value={opt}>{opt}</SelectItem>)}
            </SelectContent>
          </Select>
        );
      case 'checkbox':
        return <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} className="w-5 h-5" />;
      case 'signature':
        return <SignaturePad label={field.label} onSave={setSignature} />;
      case 'number':
        return <Input type="number" value={value} onChange={(e) => onChange(e.target.value)}
          className="bg-primary-dark border-white/20 text-white" />;
      case 'date':
        return <Input type="date" value={value} onChange={(e) => onChange(e.target.value)}
          className="bg-primary-dark border-white/20 text-white" />;
      default:
        return <Input value={value} onChange={(e) => onChange(e.target.value)}
          className="bg-primary-dark border-white/20 text-white" />;
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-white font-bold">{template?.name || submission?.form_name}</h3>
          <p className="text-white/50 text-sm">Client: {submission?.client_name}</p>
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-32 bg-primary-dark border-white/20 text-white">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
            <SelectItem value="sent">Sent</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-4 max-h-96 overflow-y-auto pr-2">
        {(template?.fields || []).map((field, i) => (
          <div key={i}>
            <Label className="text-white/70 text-sm mb-1 block">
              {field.label || field.name} {field.required && <span className="text-red-400">*</span>}
            </Label>
            {renderField(field)}
          </div>
        ))}
        {signature && (
          <div>
            <Label className="text-white/70 text-sm mb-1 block">Signature</Label>
            <img src={signature} alt="Signature" className="h-16 bg-white rounded border" />
          </div>
        )}
      </div>

      <div className="flex justify-end gap-2 pt-4 border-t border-white/10">
        <Button onClick={onClose} variant="outline" className="border-white/20 text-white/60">Cancel</Button>
        <Button onClick={handleSave} className="bg-electric-teal text-primary-dark font-bold">
          <Save className="w-4 h-4 mr-1" /> Save
        </Button>
      </div>
    </div>
  );
};

// ==================== SPREADSHEET VIEW ====================
const SpreadsheetView = ({ data, onCellClick, onExport }) => {
  const [sortCol, setSortCol] = useState(null);
  const [sortDir, setSortDir] = useState('asc');

  const handleSort = (col) => {
    if (sortCol === col) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortCol(col);
      setSortDir('asc');
    }
  };

  const sortedRows = [...(data?.rows || [])].sort((a, b) => {
    if (!sortCol) return 0;
    const av = a[sortCol] || '';
    const bv = b[sortCol] || '';
    return sortDir === 'asc' ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
  });

  return (
    <div className="bg-secondary-dark rounded-lg border border-white/10 overflow-hidden">
      <div className="flex items-center justify-between p-3 border-b border-white/10">
        <h3 className="text-white font-bold">{data?.form_name || 'Spreadsheet'}</h3>
        <Button onClick={onExport} size="sm" variant="outline" className="border-white/20 text-white/60">
          <Download className="w-4 h-4 mr-1" /> Export CSV
        </Button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-white/5">
              {(data?.columns || []).map(col => (
                <th key={col} onClick={() => handleSort(col)}
                  className="text-left text-white/60 font-medium p-3 cursor-pointer hover:bg-white/5 whitespace-nowrap">
                  {data?.field_labels?.[col] || col.replace(/_/g, ' ')}
                  {sortCol === col && <span className="ml-1">{sortDir === 'asc' ? '↑' : '↓'}</span>}
                </th>
              ))}
              <th className="w-10"></th>
            </tr>
          </thead>
          <tbody>
            {sortedRows.map((row, i) => (
              <tr key={row.id || i} className="border-t border-white/5 hover:bg-white/5">
                {(data?.columns || []).map(col => (
                  <td key={col} className="p-3 text-white/80 truncate max-w-xs">
                    {row[col] || '-'}
                  </td>
                ))}
                <td className="p-3">
                  <Button onClick={() => onCellClick(row)} variant="ghost" size="sm" className="text-electric-teal p-1">
                    <Edit2 className="w-4 h-4" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {sortedRows.length === 0 && (
          <p className="text-white/30 text-center py-8">No submissions yet</p>
        )}
      </div>
    </div>
  );
};

// ==================== MAIN FORMS TAB ====================
const FormsTab = ({ customers }) => {
  const [view, setView] = useState('templates'); // templates, submissions, spreadsheet
  const [templates, setTemplates] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [spreadsheetData, setSpreadsheetData] = useState(null);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [isFillerOpen, setIsFillerOpen] = useState(false);
  const [filterClient, setFilterClient] = useState('');
  const [filterTemplate, setFilterTemplate] = useState('');
  const [newSubmissionDialog, setNewSubmissionDialog] = useState(false);
  const [newSubmission, setNewSubmission] = useState({ clientId: '', templateId: '' });

  useEffect(() => {
    loadTemplates();
    loadSubmissions();
  }, []);

  const loadTemplates = async () => {
    try {
      const res = await api.admin.getFormTemplates();
      setTemplates(res.data);
    } catch (e) { console.error(e); }
  };

  const loadSubmissions = async () => {
    try {
      const res = await api.admin.getFormSubmissions(filterClient || null, filterTemplate || null);
      setSubmissions(res.data);
    } catch (e) { console.error(e); }
  };

  const loadSpreadsheet = async (templateId) => {
    try {
      const res = await api.admin.getSpreadsheetByForm(templateId);
      setSpreadsheetData(res.data);
      setView('spreadsheet');
    } catch (e) { toast.error('Failed to load spreadsheet'); }
  };

  const handleExportCsv = async () => {
    if (!spreadsheetData) return;
    const template = templates.find(t => t.name === spreadsheetData.form_name);
    if (!template) return;
    try {
      const res = await api.admin.exportFormCsv(template.id);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${spreadsheetData.form_name.replace(/\s+/g, '_')}.csv`;
      a.click();
    } catch (e) { toast.error('Export failed'); }
  };

  const handleDeleteTemplate = async (id) => {
    if (!window.confirm('Delete this form template?')) return;
    try {
      await api.admin.deleteFormTemplate(id);
      toast.success('Template deleted');
      loadTemplates();
    } catch (e) { toast.error('Failed to delete'); }
  };

  const handleCreateSubmission = async () => {
    if (!newSubmission.clientId || !newSubmission.templateId) {
      toast.error('Select both client and form');
      return;
    }
    try {
      await api.admin.createFormSubmission(newSubmission.templateId, newSubmission.clientId, {});
      toast.success('Form created for client');
      setNewSubmissionDialog(false);
      setNewSubmission({ clientId: '', templateId: '' });
      loadSubmissions();
    } catch (e) { toast.error('Failed to create'); }
  };

  const handleDeleteSubmission = async (id) => {
    if (!window.confirm('Delete this form submission?')) return;
    try {
      await api.admin.deleteFormSubmission(id);
      toast.success('Submission deleted');
      loadSubmissions();
    } catch (e) { toast.error('Failed to delete'); }
  };

  const openFiller = (submission) => {
    setSelectedSubmission(submission);
    setSelectedTemplate(templates.find(t => t.id === submission.form_template_id));
    setIsFillerOpen(true);
  };

  return (
    <div className="space-y-4" data-testid="forms-tab">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-3 justify-between">
        <div className="flex gap-2">
          <Button onClick={() => setView('templates')} 
            variant={view === 'templates' ? 'default' : 'outline'}
            className={view === 'templates' ? 'bg-electric-teal text-primary-dark' : 'border-white/20 text-white/60'}>
            <Grid className="w-4 h-4 mr-1" /> Templates
          </Button>
          <Button onClick={() => setView('submissions')}
            variant={view === 'submissions' ? 'default' : 'outline'}
            className={view === 'submissions' ? 'bg-electric-teal text-primary-dark' : 'border-white/20 text-white/60'}>
            <Folder className="w-4 h-4 mr-1" /> Client Forms
          </Button>
        </div>
        {view === 'templates' && (
          <Button onClick={() => { setSelectedTemplate(null); setIsEditorOpen(true); }} className="bg-electric-teal text-primary-dark font-bold">
            <Plus className="w-4 h-4 mr-1" /> New Template
          </Button>
        )}
        {view === 'submissions' && (
          <Button onClick={() => setNewSubmissionDialog(true)} className="bg-electric-teal text-primary-dark font-bold">
            <Plus className="w-4 h-4 mr-1" /> Assign Form to Client
          </Button>
        )}
      </div>

      {/* Templates View */}
      {view === 'templates' && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {templates.map((t) => (
            <div key={t.id} className="bg-secondary-dark rounded-lg border border-white/10 p-4 hover:border-electric-teal/50 transition-all">
              <div className="flex justify-between items-start mb-3">
                <div className="flex items-center gap-3">
                  {t.thumbnail_url ? (
                    <img src={t.thumbnail_url} alt="" className="w-12 h-12 rounded object-cover bg-white/10" />
                  ) : (
                    <div className="w-12 h-12 bg-electric-teal/10 rounded flex items-center justify-center">
                      <FileText className="w-6 h-6 text-electric-teal" />
                    </div>
                  )}
                  <div>
                    <h3 className="text-white font-bold">{t.name}</h3>
                    <span className="text-white/40 text-xs uppercase">{t.category}</span>
                  </div>
                </div>
              </div>
              <p className="text-white/50 text-sm mb-3 line-clamp-2">{t.description || 'No description'}</p>
              <p className="text-white/30 text-xs mb-3">{(t.fields || []).length} fields</p>
              <div className="flex gap-2">
                <Button onClick={() => loadSpreadsheet(t.id)} variant="outline" size="sm" className="border-white/20 text-white/60 flex-1">
                  <Table className="w-3 h-3 mr-1" /> Data
                </Button>
                <Button onClick={() => { setSelectedTemplate(t); setIsEditorOpen(true); }} variant="outline" size="sm" className="border-white/20 text-white/60">
                  <Edit2 className="w-3 h-3" />
                </Button>
                <Button onClick={() => handleDeleteTemplate(t.id)} variant="ghost" size="sm" className="text-red-400">
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            </div>
          ))}
          {templates.length === 0 && (
            <div className="col-span-full text-center py-12">
              <FileText className="w-12 h-12 text-white/20 mx-auto mb-3" />
              <p className="text-white/30">No form templates yet</p>
              <Button onClick={() => setIsEditorOpen(true)} className="mt-4 bg-electric-teal/20 text-electric-teal">
                Create Your First Template
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Submissions View */}
      {view === 'submissions' && (
        <div className="space-y-4">
          {/* Filters */}
          <div className="flex flex-wrap gap-2">
            <Select value={filterClient || "all"} onValueChange={(v) => { setFilterClient(v === "all" ? "" : v); }}>
              <SelectTrigger className="w-48 bg-primary-dark border-white/20 text-white">
                <SelectValue placeholder="Filter by client..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Clients</SelectItem>
                {customers.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filterTemplate || "all"} onValueChange={(v) => { setFilterTemplate(v === "all" ? "" : v); }}>
              <SelectTrigger className="w-48 bg-primary-dark border-white/20 text-white">
                <SelectValue placeholder="Filter by form..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Forms</SelectItem>
                {templates.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button onClick={loadSubmissions} variant="outline" className="border-white/20 text-white/60">
              <Filter className="w-4 h-4 mr-1" /> Apply
            </Button>
          </div>

          {/* Submissions Table */}
          <div className="bg-secondary-dark rounded-lg border border-white/10 overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-white/5">
                  <th className="text-left text-white/60 font-medium p-3">Client</th>
                  <th className="text-left text-white/60 font-medium p-3">Form</th>
                  <th className="text-left text-white/60 font-medium p-3">Status</th>
                  <th className="text-left text-white/60 font-medium p-3 hidden md:table-cell">Updated</th>
                  <th className="w-24"></th>
                </tr>
              </thead>
              <tbody>
                {submissions.map((sub) => (
                  <tr key={sub.id} className="border-t border-white/5 hover:bg-white/5">
                    <td className="p-3 text-white font-medium">{sub.client_name}</td>
                    <td className="p-3 text-white/80">{sub.form_name}</td>
                    <td className="p-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-bold uppercase ${
                        sub.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                        sub.status === 'sent' ? 'bg-blue-500/20 text-blue-400' :
                        'bg-yellow-500/20 text-yellow-400'
                      }`}>{sub.status}</span>
                    </td>
                    <td className="p-3 text-white/50 hidden md:table-cell">
                      {sub.updated_at ? new Date(sub.updated_at).toLocaleDateString() : '-'}
                    </td>
                    <td className="p-3">
                      <div className="flex gap-1">
                        <Button onClick={() => openFiller(sub)} variant="ghost" size="sm" className="text-electric-teal">
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button onClick={() => handleDeleteSubmission(sub.id)} variant="ghost" size="sm" className="text-red-400">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {submissions.length === 0 && (
              <p className="text-white/30 text-center py-8">No form submissions found</p>
            )}
          </div>
        </div>
      )}

      {/* Spreadsheet View */}
      {view === 'spreadsheet' && spreadsheetData && (
        <div>
          <Button onClick={() => setView('templates')} variant="ghost" className="text-white/60 mb-4">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back to Templates
          </Button>
          <SpreadsheetView 
            data={spreadsheetData} 
            onCellClick={(row) => {
              const sub = submissions.find(s => s.id === row.id);
              if (sub) openFiller(sub);
            }}
            onExport={handleExportCsv}
          />
        </div>
      )}

      {/* Template Editor Dialog */}
      <Dialog open={isEditorOpen} onOpenChange={setIsEditorOpen}>
        <DialogContent className="bg-secondary-dark border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-electric-teal">
              {selectedTemplate ? 'Edit Form Template' : 'Create Form Template'}
            </DialogTitle>
          </DialogHeader>
          <FormTemplateEditor 
            template={selectedTemplate} 
            onSave={() => { setIsEditorOpen(false); loadTemplates(); }}
            onClose={() => setIsEditorOpen(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Form Filler Dialog */}
      <Dialog open={isFillerOpen} onOpenChange={setIsFillerOpen}>
        <DialogContent className="bg-secondary-dark border-white/10 text-white max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-electric-teal">Fill Form</DialogTitle>
          </DialogHeader>
          {selectedSubmission && (
            <FormFiller 
              submission={selectedSubmission}
              template={selectedTemplate}
              onSave={() => { setIsFillerOpen(false); loadSubmissions(); }}
              onClose={() => setIsFillerOpen(false)}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* New Submission Dialog */}
      <Dialog open={newSubmissionDialog} onOpenChange={setNewSubmissionDialog}>
        <DialogContent className="bg-secondary-dark border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-electric-teal">Assign Form to Client</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-white/70">Select Client</Label>
              <Select value={newSubmission.clientId} onValueChange={(v) => setNewSubmission({ ...newSubmission, clientId: v })}>
                <SelectTrigger className="bg-primary-dark border-white/20 text-white mt-1">
                  <SelectValue placeholder="Choose client..." />
                </SelectTrigger>
                <SelectContent>
                  {customers.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-white/70">Select Form</Label>
              <Select value={newSubmission.templateId} onValueChange={(v) => setNewSubmission({ ...newSubmission, templateId: v })}>
                <SelectTrigger className="bg-primary-dark border-white/20 text-white mt-1">
                  <SelectValue placeholder="Choose form..." />
                </SelectTrigger>
                <SelectContent>
                  {templates.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button onClick={() => setNewSubmissionDialog(false)} variant="outline" className="border-white/20 text-white/60">Cancel</Button>
            <Button onClick={handleCreateSubmission} className="bg-electric-teal text-primary-dark font-bold">Create</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FormsTab;
